import {
  AddIcon,
  Button,
  Card,
  Chip,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DuplicateIcon,
  EditIcon,
  ExternalLinkIcon,
  ImportantIcon,
  Input,
  KeyIcon,
  LinkIcon,
  LogOutIcon,
  ShieldIcon,
} from "@bp/ui-components";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { ClientFormModal } from "../../components/AuthClients/ClientFormModal/ClientFormModal";
import { LinkedApplications } from "../../components/AuthClients/LinkedApplications/LinkedApplications";
import { SecretField } from "../../components/AuthClients/SecretField/SecretField";
import { useSetCrumbTitle } from "../../contexts/CrumbTitleContext";
import { useConfirm } from "../../hooks/useConfirm";
import { DetailRow } from "../../components/DetailRow/DetailRow";
import { showSuccessToast, showUserErrorToast } from "../../utils/toast";
import css from "./AuthClientDetailPage.module.scss";

// Colocated fragment: every field this detail page renders.
const AuthClientDetailPage_AuthClient = graphql(`
  fragment AuthClientDetailPage_AuthClient on AuthClient {
    clientId
    clientName
    clientSecret
    redirectUris
    postLogoutRedirectUris
    responseTypes
    grantTypes
    scope
    tokenEndpointAuthMethod
    idTokenSignedResponseAlg
    logoUri
    tosUri
    policyUri
    tksExtensionPkceOptional
    tksExtensionConsentOptional
    tksExtensionLicenseOptional
  }
`);

const AuthClientDetailPageQuery = graphql(`
  query AuthClientDetailPage_Query($clientId: ID!) {
    authClients(where: { clientId: $clientId }) {
      ...AuthClientDetailPage_AuthClient
      ...ClientFormModal_AuthClient
    }
  }
`);

const DeleteAuthClientsMutation = graphql(`
  mutation AuthClientDetailPage_DeleteAuthClients($clientId: ID!) {
    deleteAuthClients(where: { clientId: $clientId }) {
      nodesDeleted
    }
  }
`);

const UpdateAuthClientsMutation = graphql(`
  mutation AuthClientDetailPage_UpdateAuthClients($clientId: ID!, $update: AuthClientUpdateInput!) {
    updateAuthClients(where: { clientId: $clientId }, update: $update) {
      authClients {
        clientId
      }
    }
  }
`);

// Scope classification (labels + sensitivity) mirrors @bp/pim-auth-constants /
// the prototype's SCOPE_INFO — static domain knowledge, not a schema field.
const SCOPE_INFO: Record<string, { label: string; sensitive?: boolean }> = {
  openid: { label: "Identitäts-Token (sub)" },
  profile: { label: "Name, Profilangaben" },
  email: { label: "E-Mail-Adresse" },
  offline_access: { label: "Refresh-Token (dauerhafter Login)", sensitive: true },
  roles: { label: "Rollen-Claims" },
  user: { label: "Konto-Stammdaten" },
  other_profiles: { label: "Verknüpfte Profile (z. B. Kinder)", sensitive: true },
  child_profiles: { label: "Profile betreuter Kinder", sensitive: true },
  cloud: { label: "Cloud-Speicher-Zugriff", sensitive: true },
  omni_organization: { label: "Organisationsübergreifender Zugriff", sensitive: true },
};

function ScopeRow({ scope }: { scope: string }) {
  const info = SCOPE_INFO[scope] ?? { label: scope };
  return (
    <div className={css.scopeRow}>
      <span
        className={classNames(css.scopeIc, info.sensitive ? css.scopeIcSensitive : css.scopeIcCore)}
      >
        {info.sensitive ? (
          <ImportantIcon style={{ width: 15, height: 15 }} />
        ) : (
          <CheckIcon style={{ width: 15, height: 15 }} />
        )}
      </span>
      <div className={css.scopeMain}>
        <div className={css.scopeName}>
          <code className={css.mono}>{scope}</code>
        </div>
        <div className={css.scopeMeta}>{info.label}</div>
      </div>
      {info.sensitive && <Chip value="Sensibel" isSmall />}
    </div>
  );
}

function UriListCard({
  title,
  headIcon,
  rowIcon,
  uris,
  placeholder,
  onAdd,
  onRemove,
}: {
  title: string;
  headIcon: React.ReactNode;
  rowIcon: React.ReactNode;
  uris: readonly string[];
  placeholder: string;
  onAdd: (uri: string) => void;
  onRemove: (uri: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState("");
  const commit = () => {
    const v = value.trim();
    if (!v) {
      return;
    }
    onAdd(v);
    setValue("");
    setAdding(false);
  };
  const copy = (u: string) => {
    void navigator.clipboard?.writeText(u);
    showSuccessToast("URI kopiert");
  };
  return (
    <Card contentPadding="none">
      <div className={css.cardHead}>
        <span className={css.cardHeadTitle}>
          {headIcon}
          {title}
        </span>
        <div className={css.cardHeadActions}>
          <Button hierarchy="tertiary" icon={<AddIcon />} onClick={() => setAdding((a) => !a)}>
            Hinzufügen
          </Button>
        </div>
      </div>
      <div>
        {adding && (
          <div className={classNames(css.uriRow, css.uriRowAdd)}>
            {rowIcon}
            <div className={css.uriInput}>
              <Input
                name="newUri"
                value={value}
                placeholder={placeholder}
                autoFocus
                dense
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commit();
                  }
                  if (e.key === "Escape") {
                    setAdding(false);
                    setValue("");
                  }
                }}
              />
            </div>
            <Button hierarchy="primary" height="s" onClick={commit} disabled={!value.trim()}>
              Speichern
            </Button>
            <Button
              hierarchy="tertiary"
              height="s"
              icon={<CloseIcon />}
              ariaLabel="Abbrechen"
              onClick={() => {
                setAdding(false);
                setValue("");
              }}
            />
          </div>
        )}
        {uris.map((u) => (
          <div key={u} className={css.uriRow}>
            {rowIcon}
            <code className={css.uriVal}>{u}</code>
            <Button
              hierarchy="tertiary"
              height="s"
              icon={<DuplicateIcon />}
              ariaLabel="Kopieren"
              onClick={() => copy(u)}
            />
            <Button
              hierarchy="tertiary"
              height="s"
              icon={<DeleteIcon />}
              ariaLabel="URI entfernen"
              onClick={() => onRemove(u)}
            />
          </div>
        ))}
        {uris.length === 0 && !adding && (
          <div className={css.uriEmpty}>Keine Einträge hinterlegt.</div>
        )}
      </div>
    </Card>
  );
}

export function AuthClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [{ data }] = useQuery({
    query: AuthClientDetailPageQuery,
    variables: { clientId: clientId ?? "" },
  });
  const raw = data?.authClients?.[0];
  const client = getFragmentData(AuthClientDetailPage_AuthClient, raw);
  const [editing, setEditing] = useState(false);
  const [, deleteClient] = useMutation(DeleteAuthClientsMutation);
  const [, updateClient] = useMutation(UpdateAuthClientsMutation);
  const { confirm, ConfirmationDialog } = useConfirm();

  const onDelete = async () => {
    if (!client) {
      return;
    }
    const ok = await confirm({
      title: "OAuth-Client löschen",
      message: `„${client.clientName ?? client.clientId}" wird entfernt. Bestehende Token bleiben bis zum Ablauf gültig, neue werden nicht mehr ausgegeben.`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    const res = await deleteClient({ clientId: client.clientId });
    if (res.error) {
      showUserErrorToast({ text: "Löschen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("OAuth-Client gelöscht");
    navigate("/auth-clients");
  };

  const title = client?.clientName ?? client?.clientId;
  useSetCrumbTitle(title);
  useEffect(() => {
    const previous = document.title;
    document.title = `${title ?? "OAuth-Client"} · Schulsuite-Admin`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  if (!raw || !client) {
    return <p style={{ color: "var(--color-grey)" }}>OAuth-Client nicht gefunden.</p>;
  }

  const scopes = (client.scope ?? "").split(/\s+/).filter(Boolean);
  const grantTypes = client.grantTypes ?? [];
  const responseTypes = client.responseTypes ?? [];
  const isPublic = client.tokenEndpointAuthMethod === "none";
  const copyClientId = () => {
    void navigator.clipboard?.writeText(client.clientId);
    showSuccessToast("ClientId kopiert");
  };
  const saveUris = async (
    field: "redirectUris" | "postLogoutRedirectUris",
    next: string[],
    message: string,
  ) => {
    const update =
      field === "redirectUris" ? { redirectUris: next } : { postLogoutRedirectUris: next };
    const res = await updateClient({ clientId: client.clientId, update });
    if (res.error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(message);
  };

  return (
    <>
      <div className={css.stack}>
        {/* ── header card ── */}
        <Card contentPadding="none">
          <div className={css.headerRow}>
            <div className={css.headerLeft}>
              <span className={css.markSquare}>
                <KeyIcon style={{ width: 24, height: 24, color: "var(--color-primary)" }} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className={css.titleRow}>
                  <span className={css.title}>{client.clientName ?? client.clientId}</span>
                  <Chip value={isPublic ? "Public" : "Confidential"} isSmall />
                </div>
                <div className={css.metaRow}>
                  <button
                    type="button"
                    className={css.idPill}
                    title="ClientId kopieren"
                    onClick={copyClientId}
                  >
                    {client.clientId}
                  </button>
                  <Button
                    hierarchy="tertiary"
                    height="s"
                    icon={<DuplicateIcon />}
                    ariaLabel="ClientId kopieren"
                    onClick={copyClientId}
                  />
                </div>
              </div>
            </div>
            <div className={css.headerActions}>
              <Button hierarchy="secondary" icon={<EditIcon />} onClick={() => setEditing(true)}>
                Bearbeiten
              </Button>
              <Dropdown
                trigger={
                  <Button
                    hierarchy="secondary"
                    icon={<DotsHorizontalIcon />}
                    ariaLabel="Weitere Aktionen"
                  />
                }
              >
                <DropdownMenu
                  data={[
                    { label: "ClientId kopieren", onClick: copyClientId },
                    { label: "Löschen", color: "error", onClick: () => void onDelete() },
                  ]}
                />
              </Dropdown>
            </div>
          </div>
        </Card>

        {/* ── two-column body ── */}
        <div className={css.grid}>
          {/* left column */}
          <div className={css.stack}>
            <UriListCard
              title="Redirect URIs"
              headIcon={
                <LinkIcon style={{ width: 17, height: 17, color: "var(--color-primary)" }} />
              }
              rowIcon={
                <ExternalLinkIcon
                  style={{ width: 15, height: 15, color: "var(--color-grey-light)" }}
                />
              }
              uris={client.redirectUris ?? []}
              placeholder="https://app.example.de/auth/callback"
              onAdd={(u) =>
                void saveUris(
                  "redirectUris",
                  [...(client.redirectUris ?? []), u],
                  "Redirect-URI hinzugefügt",
                )
              }
              onRemove={(u) =>
                void saveUris(
                  "redirectUris",
                  (client.redirectUris ?? []).filter((x) => x !== u),
                  "Redirect-URI entfernt",
                )
              }
            />

            <UriListCard
              title="Post-Logout Redirect URIs"
              headIcon={
                <LogOutIcon style={{ width: 17, height: 17, color: "var(--color-primary)" }} />
              }
              rowIcon={
                <LogOutIcon style={{ width: 15, height: 15, color: "var(--color-grey-light)" }} />
              }
              uris={client.postLogoutRedirectUris ?? []}
              placeholder="https://app.example.de/"
              onAdd={(u) =>
                void saveUris(
                  "postLogoutRedirectUris",
                  [...(client.postLogoutRedirectUris ?? []), u],
                  "Post-Logout-URI hinzugefügt",
                )
              }
              onRemove={(u) =>
                void saveUris(
                  "postLogoutRedirectUris",
                  (client.postLogoutRedirectUris ?? []).filter((x) => x !== u),
                  "Post-Logout-URI entfernt",
                )
              }
            />

            <Card contentPadding="none">
              <div className={css.cardHead}>
                <span className={css.cardHeadTitle}>
                  <ShieldIcon style={{ width: 17, height: 17, color: "var(--color-primary)" }} />
                  Scope
                </span>
                <div className={css.cardHeadActions}>
                  <Button hierarchy="tertiary" icon={<EditIcon />} onClick={() => setEditing(true)}>
                    Bearbeiten
                  </Button>
                </div>
              </div>
              <div className={css.bodyPad}>
                {scopes.length > 0 ? (
                  scopes.map((s) => <ScopeRow key={s} scope={s} />)
                ) : (
                  <span className={css.muted}>—</span>
                )}
              </div>
            </Card>
          </div>

          {/* right column */}
          <div className={css.stack}>
            <Card>
              <div className={css.h3}>Konfiguration</div>
              <DetailRow label="Token auth method">
                <code className={css.codepill}>{client.tokenEndpointAuthMethod}</code>
              </DetailRow>
              <DetailRow label="ID-Token Alg">{client.idTokenSignedResponseAlg || "—"}</DetailRow>
              <DetailRow label="Grant Types">
                <div className={css.chipRow}>
                  {grantTypes.length > 0 ? (
                    grantTypes.map((g) => (
                      <code key={g} className={css.codepill}>
                        {g}
                      </code>
                    ))
                  ) : (
                    <span className={css.muted}>—</span>
                  )}
                </div>
              </DetailRow>
              <DetailRow label="Response types">
                <div className={css.chipRow}>
                  {responseTypes.length > 0 ? (
                    responseTypes.map((r) => (
                      <code key={r} className={css.codepill}>
                        {r}
                      </code>
                    ))
                  ) : (
                    <span className={css.muted}>—</span>
                  )}
                </div>
              </DetailRow>
            </Card>

            {client.clientSecret ? (
              <Card>
                <div className={css.h3}>Client Secret</div>
                <SecretField value={client.clientSecret} />
                <div className={css.hint}>
                  Keine automatische Rotation — das Secret wird über „Bearbeiten“ neu gesetzt und
                  ersetzt das bisherige sofort.
                </div>
              </Card>
            ) : isPublic ? (
              <div className={css.gated}>
                <span className={css.gatedIc}>
                  <ShieldIcon style={{ width: 18, height: 18 }} />
                </span>
                <div className={css.gatedMain}>
                  <div className={css.gatedTitle}>Kein Secret (Public Client)</div>
                  <div className={css.gatedText}>
                    <code className={css.mono}>token_endpoint_auth_method = none</code> —
                    Absicherung über PKCE.
                  </div>
                </div>
              </div>
            ) : (
              <Card>
                <div className={css.h3}>Client Secret</div>
                <span className={css.muted}>Secret aus Sicherheitsgründen nicht abrufbar.</span>
              </Card>
            )}

            <Card>
              <div className={css.h3}>TKS-Erweiterungen</div>
              <DetailRow label="PKCE optional">
                {client.tksExtensionPkceOptional ? (
                  <Chip value="aktiv" isSmall />
                ) : (
                  <span className={css.muted}>aus</span>
                )}
              </DetailRow>
              <DetailRow label="Einwilligung optional">
                {client.tksExtensionConsentOptional ? (
                  <Chip value="aktiv" isSmall />
                ) : (
                  <span className={css.muted}>aus</span>
                )}
              </DetailRow>
              <DetailRow label="Lizenzprüfung optional">
                {client.tksExtensionLicenseOptional ? (
                  <Chip value="aktiv" isSmall />
                ) : (
                  <span className={css.muted}>aus</span>
                )}
              </DetailRow>
            </Card>

            {(client.logoUri || client.tosUri || client.policyUri) && (
              <Card>
                <div className={css.h3}>Branding & Rechtliches</div>
                {client.logoUri && (
                  <DetailRow label="Logo">
                    <a href={client.logoUri} target="_blank" rel="noreferrer">
                      {client.logoUri}
                    </a>
                  </DetailRow>
                )}
                {client.tosUri && (
                  <DetailRow label="Nutzungsbed.">
                    <a href={client.tosUri} target="_blank" rel="noreferrer">
                      {client.tosUri}
                    </a>
                  </DetailRow>
                )}
                {client.policyUri && (
                  <DetailRow label="Datenschutz">
                    <a href={client.policyUri} target="_blank" rel="noreferrer">
                      {client.policyUri}
                    </a>
                  </DetailRow>
                )}
              </Card>
            )}

            <LinkedApplications clientId={client.clientId} />

            <div className={classNames(css.gated, css.gatedDanger)}>
              <span className={classNames(css.gatedIc, css.gatedIcDanger)}>
                <DeleteIcon style={{ width: 18, height: 18 }} />
              </span>
              <div className={css.gatedMain}>
                <div className={css.gatedTitle}>Client löschen</div>
                <div className={css.gatedText}>
                  Bestehende Token bleiben bis zum Ablauf gültig, neue werden nicht mehr ausgegeben.
                </div>
              </div>
              <Button hierarchy="secondary" height="s" onClick={() => void onDelete()}>
                Löschen
              </Button>
            </div>
          </div>
        </div>
      </div>
      {editing && (
        <ClientFormModal
          mode="edit"
          client={raw}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      <ConfirmationDialog />
    </>
  );
}
